import { useQuery, keepPreviousData } from '@tanstack/react-query';
import React__default from 'react';
import { memberPermissionsQueryKeys } from './memberPermissionsQueryKeys.js';
import '@fabric-app/compat/capabilities/sentry';
import { produce } from 'immer';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';
import { useWoody } from '../../woody/woody.js';
import { isQueryEnabled } from '@fabric-app/queries/utilities';
import { useContextAccessToken } from '../contexts/AnonShareTokenContext.js';
import { isWoodyError } from '../../errors/errors.utils.js';
import { sortPermissionsByRole, roleOrder } from '../utils/sortByPermissionRole.js';

const defaultData = [];
const useQueryResourceMemberPermissions = (args, queryOptions) => {
  const { data: user } = useQueryUserInfo();
  const { client } = useWoody();
  const isLoggedIn = !!user;
  const { resourceId } = args;
  const includeIndirect = args.includeIndirect ?? true;
  const enabled = isQueryEnabled([
    !!resourceId,
    isLoggedIn,
    queryOptions?.enabled
  ]);
  const accessToken = useContextAccessToken();
  const query = useQuery({
    queryKey: memberPermissionsQueryKeys.byResourceId(resourceId),
    queryFn: async (params) => {
      const resourceIdSafe = params.queryKey[1];
      const res = await client.v2({
        endpoint: "/v2/resources/{resourceId}/permissions",
        params: {
          resourceId: resourceIdSafe
        }
      }, {
        query: {
          accessToken,
          includeIndirect
        }
      });
      return res;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    // small stale time so it doesn't refetch everytime simple space is rendered when filtering
    staleTime: 1e3 * 15,
    retry(failureCount, error) {
      if (isWoodyError(error) && error.status === 404) return false;
      return failureCount < 3;
    },
    ...queryOptions,
    enabled
  });
  const permissions = query.data || defaultData;
  const { membersWithRole, isCurrentUserMember, directPermissions, usersWithIndirectPermissions } = React__default.useMemo(() => {
    const membersWithRole2 = permissions.map((permission) => ({
      ...permission.user,
      role: permission.role
    }));
    const isCurrentUserMember2 = !!membersWithRole2?.some((member) => member.id === user?.id);
    const directPermissions2 = permissions.filter((item) => item.resourceId === resourceId && item.role !== "owner").sort(sortPermissionsByRole("ASC"));
    let usersWithIndirectPermissions2 = [];
    permissions.forEach((perm) => {
      if (perm.resourceId === resourceId || perm.role === "owner" || directPermissions2.find((p) => p.userId === perm.userId)) ; else {
        usersWithIndirectPermissions2 = produce(usersWithIndirectPermissions2, (draft) => {
          const addedUserIndex = draft.findIndex((up) => up.user.id === perm.user.id);
          if (addedUserIndex >= 0) {
            draft[addedUserIndex].resources = [
              ...draft[addedUserIndex].resources,
              {
                resourceId: perm.resourceId,
                role: perm.role
              }
            ];
          } else {
            draft.push({
              user: perm.user,
              resources: [
                {
                  resourceId: perm.resourceId,
                  role: perm.role
                }
              ]
            });
          }
        });
      }
    });
    const usersWithIndirectPermissionsWithMaxRole = usersWithIndirectPermissions2.map((userWithPermissions) => {
      return {
        ...userWithPermissions,
        maxResourceRole: userWithPermissions.resources.reduce((acc, resource) => {
          return roleOrder[acc.role] > roleOrder[resource.role] ? resource : acc;
        }, userWithPermissions.resources[0])
      };
    });
    return {
      membersWithRole: membersWithRole2,
      isCurrentUserMember: isCurrentUserMember2,
      directPermissions: directPermissions2,
      usersWithIndirectPermissions: usersWithIndirectPermissionsWithMaxRole
    };
  }, [
    permissions,
    user?.id,
    resourceId
  ]);
  return {
    ...query,
    permissionsWithUser: permissions,
    membersWithRole,
    directPermissions,
    usersWithIndirectPermissions,
    /**
    * @deprecated
    * ResourceDetail.role will be a new property to check against if the user is member or not
    */
    isCurrentUserMember
  };
};

export { useQueryResourceMemberPermissions };
//# sourceMappingURL=useQueryResourceMemberPermissions.js.map
